import React, { useState } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardContent, Chip,
  Alert, TextField, FormControlLabel, Switch, Divider,
} from '@mui/material';
import { 
  CreditCard as CreditCardIcon, 
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const BillingStep = ({
  formData,
  setFormData,
  onSubmit,
  submitting,
  isCompleted,
}) => {
  const [errors, setErrors] = useState({});
  const [selectedPlan, setSelectedPlan] = useState('basic');

  const subscriptionPlans = [
    {
      id: 'basic',
      name: 'Basic',
      price: 0,
      period: '14 days',
      features: [
        'Free trial for 14 days',
        'Basic patient management',
        'Appointment scheduling',
        'Email support',
        'Up to 50 patients',
      ],
      popular: false,
    },
    {
      id: 'pro',
      name: 'Professional',
      price: 999,
      period: 'month',
      features: [
        'All Basic features',
        'Advanced analytics',
        'Priority support',
        'Unlimited patients',
        'Custom branding',
        'Telehealth integration',
        'Prescription management',
      ],
      popular: true,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 1999,
      period: 'month',
      features: [
        'All Professional features',
        'Multi-location support',
        'API access',
        'Dedicated account manager',
        'Custom integrations',
        'Advanced reporting',
        'Team management',
      ],
      popular: false,
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    // No validation required for trial - it's optional
    // If they want to add referral code, validate it
    if (formData.referral_code && formData.referral_code.trim().length < 3) {
      newErrors.referral_code = 'Referral code must be at least 3 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const data = {
        selected_plan: selectedPlan,
        referral_code: formData.referral_code || '',
        start_trial: true,
      };
      onSubmit(data);
    }
  };

  const getSelectedPlan = () => {
    return subscriptionPlans.find(plan => plan.id === selectedPlan);
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Billing & Subscription
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Choose your subscription plan and start your free trial
      </Typography>

      {isCompleted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Billing setup has been completed successfully!
        </Alert>
      )}

      {/* Trial Banner */}
      <Alert 
        severity="info" 
        icon={<StarIcon />}
        sx={{ mb: 4 }}
      >
        <Typography variant="h6" gutterBottom>
          🎉 Start Your Free Trial Today!
        </Typography>
        <Typography variant="body2">
          You get 14 days of free access to all features. No credit card required to start.
          You can upgrade to a paid plan anytime during or after the trial.
        </Typography>
      </Alert>

      {/* Subscription Plans */}
      <Typography variant="h6" gutterBottom>
        Choose Your Plan
      </Typography>
      
      <Grid container spacing={3} mb={4}>
        {subscriptionPlans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            <Card 
              variant={selectedPlan === plan.id ? "elevation" : "outlined"}
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                border: selectedPlan === plan.id ? 2 : 1,
                borderColor: selectedPlan === plan.id ? 'primary.main' : 'divider',
                position: 'relative',
                '&:hover': { 
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
              onClick={() => !isCompleted && setSelectedPlan(plan.id)}
            >
              {plan.popular && (
                <Chip
                  label="Most Popular"
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 1,
                  }}
                />
              )}
              
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom>
                  {plan.name}
                </Typography>
                
                <Box display="flex" alignItems="baseline" mb={2}>
                  <Typography variant="h4" component="span" color="primary">
                    ₹{plan.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" ml={1}>
                    /{plan.period}
                  </Typography>
                </Box>
                
                {plan.price === 0 && (
                  <Typography variant="body2" color="success.main" fontWeight="bold" mb={2}>
                    Free for 14 days
                  </Typography>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                <Box component="ul" sx={{ pl: 0, listStyle: 'none' }}>
                  {plan.features.map((feature, index) => (
                    <Box component="li" key={index} display="flex" alignItems="center" mb={1}>
                      <CheckCircleIcon 
                        color="success" 
                        sx={{ fontSize: 16, mr: 1 }} 
                      />
                      <Typography variant="body2">
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Referral Code */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Referral Code (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            If you were referred by another doctor, enter their referral code to get additional benefits.
          </Typography>
          
          <TextField
            fullWidth
            label="Referral Code"
            value={formData.referral_code || ''}
            onChange={(e) => handleInputChange('referral_code', e.target.value)}
            error={!!errors.referral_code}
            helperText={errors.referral_code || 'Enter referral code if you have one'}
            disabled={isCompleted}
            placeholder="e.g., DR123456"
          />
        </CardContent>
      </Card>

      {/* Selected Plan Summary */}
      {getSelectedPlan() && (
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Plan Summary
            </Typography>
            
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body1">
                {getSelectedPlan()?.name} Plan
              </Typography>
              <Typography variant="h6" color="primary">
                ₹{getSelectedPlan()?.price === 0 ? 'Free Trial' : getSelectedPlan()?.price}
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              {getSelectedPlan()?.price === 0 
                ? '14-day free trial, no credit card required'
                : `Billed monthly, cancel anytime`
              }
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Important Notes */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Important:</strong>
        </Typography>
        <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
          <li>Your free trial starts immediately</li>
          <li>No credit card required to start</li>
          <li>You can upgrade or cancel anytime</li>
          <li>All features are available during trial</li>
        </Typography>
      </Alert>

      {!isCompleted && (
        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={<CreditCardIcon />}
          >
            {submitting ? 'Starting Trial...' : 'Start Free Trial'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BillingStep;
